import React, { useState, useRef } from "react";
import CommonTable from "components/ReactTable/Table";
// reactstrap components
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Label,
  FormGroup,
  Form,
  Input,
  Row,
  Col,
  Container,
  CardFooter,
  Badge,
  UncontrolledTooltip,
} from "reactstrap";
import RightPanelCard from "components/Common/RightPanelCard";
import {
  fixedWidthCustomStyle,
  MASTER_STATUS_OPTIONS,
  customStyle,
  CREATED_BY,
} from "components/Common/constants";
import Select, { components } from "react-select";
import { showAlert } from "components/Common/commonFunctions";
import {
  addManufacturer,
  editManufacturer,
  getManufacturerList,
  get_manufacturer_by_ID,
} from "actions/manufacturer";
import { manufacturerList } from "constants/manufacturer";
import NotificationAlert from "react-notification-alert";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DEFAULT_RECORDS_PER_PAGE } from "components/Common/constants";

const ManufacturerMaster = () => {
  const dispatch = useDispatch();
  const manufacturersList = useSelector(
    (state) => state.manufacturer.manufacturerList,
  );
  const [manufacturerModal, setManufacturerModal] = useState(false);
  const [editState, setEditState] = useState({
    mode: "ADD",
    manufacturer: null,
  });
  const [filterState, setFilterState] = useState({
    searchName: "",
    searchStatus: null,
    createdBy: null,
    filterParams: [],
  });
  const notificationAlertRef = useRef(null);
  const [pageNum, setPageNum] = useState(1);
  const [manufacturerListState, setManufacturerListState] = useState({
    isLoading: true,
    isSubmitting: false,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const initialFormData = {
    manufacturerName: { value: "", error: "" },
    createdBy: { value: "", error: "" },
    status: "ACTIVE",
  };
  const [formData, setFormData] = useState(initialFormData);
  const [submitted, setSubmitted] = useState(false);

  const buildFilterParams = () => {
    const filterParams = [];

    if (filterState.searchName) {
      filterParams.push({
        field: "Name",
        condition: "like",
        filterValues: [filterState.searchName],
      });
    }

    if (filterState.createdBy?.value) {
      filterParams.push({
        field: "CreatedBy",
        condition: "eq",
        filterValues: [filterState.createdBy.value],
      });
    }

    if (filterState.searchStatus) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filterState.searchStatus.value],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  useEffect(() => {
    getManufacturerList(dispatch, {
      pageNum: pageNum || 1,
      filters: buildFilterParams(),
    });

    return () => {
      dispatch({ type: manufacturerList, payload: {} }); //to clear the manufacturerList redux state on unmount
    };
  }, [dispatch, pageNum, filterState.filterParams]);

  useEffect(() => {
    if (manufacturersList?.code === 200) {
      // Using manufacturersList (from Redux)
      setManufacturerListState({
        isLoading: false,
        error: "",
        noRecords: manufacturersList?.data?.manufacturer?.length === 0, // Consistently use manufacturersList
        showLoaderOnClick: false,
      });
    } else if (manufacturersList?.code || manufacturersList?.message) {
      // Using manufacturersList (from Redux)
      setManufacturerListState({
        isLoading: false,
        error:
          manufacturersList?.message ||
          "Failed to load the manufacturers list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [manufacturersList]);

  const isFormInvalid =
  submitted &&
  (!formData.manufacturerName.value.trim() ||
    !!formData.manufacturerName.error);

  const formattedManufacturerList =
    manufacturersList?.data?.manufacturer?.map((item, index) => {
      return {
        ...item,
        srNo:
          (pageNum - 1) *
          (manufacturersList?.data?.noOfRecordsPerPage ||
            DEFAULT_RECORDS_PER_PAGE) +
          index +
          1,
      };
    }) ?? [];

  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  const handleSearch = () => {
    const newfilterParams = buildFilterParams();
    if (
      JSON.stringify(filterState.filterParams) !==
      JSON.stringify(newfilterParams)
    ) {
      setManufacturerListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));
      setFilterState((prev) => ({
        ...prev,
        filterParams: newfilterParams,
      }));
      if (pageNum !== 1) setPageNum(1);
    }
  };

  const handleInputChange = (name, value) => {
    if (name === "status") {
      setFormData((prevState) => ({
        ...prevState,
        status: value,
      }));
      return;
    }

    setFormData((prevState) => ({
      ...prevState,
      [name]: {
        ...prevState[name],
        value,
        error: "",
      },
    }));
  };

  const toggleManufacturerModal = () => {
    setManufacturerModal(!manufacturerModal);
    setSubmitted(false);
    if (!manufacturerModal) {
      setFormData(initialFormData);
      setEditState({ mode: "ADD", manufacturer: null });
    }
  };

  // open modal for ADD
  const openAddModal = () => {
    setSubmitted(false);
    setEditState({ mode: "ADD", manufacturer: true });
    setFormData(initialFormData);
    setManufacturerModal(true);
  };

  // open modal for EDIT and fetch data by ID
  const openEditModal = async (manufacturer) => {
    setSubmitted(false);
    setEditState({ mode: "EDIT", manufacturer });
    try {
      const res = await get_manufacturer_by_ID(manufacturer.ID);
      if (res.code === 200 && res.data) {
        const m = res.data;
        setEditState({ mode: "EDIT", manufacturer: m });
        setFormData({
          manufacturerName: { value: m.name || "", error: "" },
          status: m.status,
          createdBy: { value: m.createdBy || "", error: "" },
        });
        setManufacturerModal(true);
      } else {
        notify(
          "danger",
          res.message || "Failed to fetch manufacturer details!",
        );
      }
    } catch (err) {
      notify("danger", "Error fetching manufacturer details!");
    } finally {
      setManufacturerListState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  // handle form submit (add/edit)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!formData.manufacturerName.value.trim()) {
      handleOnBlur("manufacturerName", formData.manufacturerName.value);
      return;
    }

    const bodyData = {
      name: formData.manufacturerName.value.trim(),
      status: formData.status,
      ...(editState.mode === "EDIT" && {
        createdBy: formData.createdBy.value,
      }),
    };

    try {
      setManufacturerListState((prev) => ({ ...prev, isSubmitting: true }));
      let res;

      if (editState.mode === "ADD") res = await addManufacturer(bodyData);
      else res = await editManufacturer(editState.manufacturer.ID, bodyData);

      if (res.code === 200) {
        notify(
          "success",
          res.message ||
          `Manufacturer ${editState.mode === "ADD" ? "added" : "updated"} successfully!`,
        );
        toggleManufacturerModal();

        getManufacturerList(dispatch, pageNum, buildFilterParams());
      } else {
        const message =
          res.message ||
          `Failed to ${editState.mode === "ADD" ? "add" : "update"} manufacturer!`;
        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        `Failed to ${editState.mode === "ADD" ? "add" : "update"} manufacturer!`,
      );
    } finally {
      setManufacturerListState((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  const getValidationError = (name, value) => {
    switch (name) {
      case "manufacturerName":
        return !value
          ? "Please enter a manufacturer name"
          : value.length < 2
            ? "Manufacturer name must be at least 2 characters"
            : value.length > 50
              ? "Manufacturer name must be less than 50 characters"
              : "";

      case "createdBy":
        return editState.mode === "EDIT" && !value
          ? "Please select created by"
          : "";

      default:
        return "";
    }
  };

  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const handleStatusToggle = async (manufacturer) => {
    const currentStatus = manufacturer.status;
    const newStatus = currentStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    const actionText = currentStatus === "ACTIVE" ? "inactivate" : "activate";

    showAlert({
      title: `Are you sure you want to ${actionText} this manufacturer?`,
      message: `Manufacturer "${manufacturer.name}" will become ${newStatus.toLowerCase()}.`,
      type: "warning",
      confirmStyle: "default",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          setManufacturerListState((prev) => ({ ...prev, isSubmitting: true }));
          const bodyData = {
            status: newStatus,
          };
          const res = await editManufacturer(manufacturer.ID, bodyData);
          if (res.code === 200) {
            notify(
              "success",
              res.message || `Manufacturer ${actionText}d successfully!`,
            );

            getManufacturerList(dispatch, pageNum, buildFilterParams());
          } else {
            const message =
              res?.message || `Failed to ${actionText} manufacturer!`;
            notify("danger", message);
          }
        } catch (error) {
          notify("danger", `Failed to ${actionText} manufacturer!`);
        } finally {
          setManufacturerListState((prev) => ({
            ...prev,
            isSubmitting: false,
          }));
        }
      },
      onCancel: () => { },
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      // e.preventDefault();
      handleSearch();
    }
  };

  const handleCLear = () => {
    setManufacturerListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    setFilterState({
      searchName: "",
      searchStatus: null,
      createdBy: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  // Custom SingleValue Renderer (for the selected value)
  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  const columns = [
  { Header: "Sr. no.", accessor: "srNo", className: "col-srNo" },
  { Header: "Manufacturer Name", accessor: "name", className: "col-mfr-name" },
  {
    Header: "Created By",
    accessor: "createdBy",
    className: "col-mfr-createdBy", // added
    Cell: ({ value }) =>
      CREATED_BY.find((item) => item.value === value)?.label || "-",
  },
  {
    Header: "Status",
    accessor: "status",
    className: "col-mfr-status", // added
    Cell: ({ value }) => (
      <Badge color={value === "ACTIVE" ? "success" : "warning"} pill>
        {value}
      </Badge>
    ),
  },
  {
    Header: "Actions",
    accessor: "actions",
    className: "col-mfr-actions", // changed from "text-center"
    disableSortBy: true,
    Cell: ({ row }) => (
      <div className="actions-right d-flex justify-content-start">
        <Button
          onClick={() => openEditModal(row.original)}
          color="link"
          size="sm"
          className="btn-icon btn-neutral ml-1 icon-info"
          id={`edit-${row.original.ID}`}
        >
          <i className="tim-icons icon-pencil tim-md" />
        </Button>
        <UncontrolledTooltip delay={0} target={`edit-${row.original.ID}`}>
          Edit
        </UncontrolledTooltip>
        <Button
          onClick={() => handleStatusToggle(row.original)}
          color="link"
          size="sm"
          className="btn-icon btn-neutral ml-1"
          id={`status-${row.original.ID}`}
          disabled={manufacturerListState.isSubmitting}
        >
          <>
            {row.original.status === "ACTIVE" ? (
              <i className="fa fa-xmark custom-icon btn-icon icon-danger" />
            ) : (
              <i className="fa fa-check custom-icon btn-icon icon-success" />
            )}
          </>
        </Button>
        <UncontrolledTooltip delay={0} target={`status-${row.original.ID}`}>
          {row.original.status === "ACTIVE" ? "Inactivate" : "Activate"}{" "}
          Manufacturer
        </UncontrolledTooltip>
      </div>
    ),
  },
];

  return (
    <>
      <Container fluid className="content mt-4">
        <div className="rna-container">
          <NotificationAlert ref={notificationAlertRef} />
        </div>
        {alert}
        <Row>
          <Col md="12">
            <Card className="card-user">
              <CardHeader className="d-flex justify-content-between align-items-center">
                <CardTitle tag="h3" className="font-weight-normal">
                  Manufacturers
                </CardTitle>{" "}
                <Button
                  color="default"
                  onClick={openAddModal}
                  className="btn-round btn-style"
                >
                  <i className="fa fa-plus mr-1" />
                  Manufacturer
                </Button>
              </CardHeader>

              <CardBody>
                <div className="d-flex align-items-center justify-content-between flex-wrap flex-column flex-md-row">
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by manufacturer"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchName}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchName: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                      maxLength={150}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={CREATED_BY}
                      placeholder="Created by"
                      value={filterState.createdBy}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          createdBy: selected,
                        }))
                      }
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={MASTER_STATUS_OPTIONS}
                      placeholder="Status"
                      value={filterState.searchStatus}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchStatus: selected,
                        }))
                      }
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      components={{ Option, SingleValue }}
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      className="d-flex align-items-center justify-content-center mb-3 filter-btn btn-simple mt-2 mr-1"
                      onClick={handleSearch}
                    >
                      {manufacturerListState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin search-loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {filterState.filterParams.length > 0 && (
                      <Button
                        color="default"
                        className="d-flex align-items-center mb-3 filter-btn btn-simple mt-2"
                        onClick={handleCLear}
                      >
                        <i className="fa fa-times mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
                <div className="mt-2 mb-3 mx-1">
                  {manufacturersList?.data?.totalRecords ? (
                    <>
                      {filterState.filterParams.length == 0 ? (
                        <span className="font-weight-bold">
                          Total Manufacturer :{" "}
                          {manufacturersList?.data?.totalRecords}
                        </span>
                      ) : (
                        <span className="font-weight-bold">
                          Showing{" "}
                          {manufacturersList?.data?.totalFilteredRecords} of{" "}
                          {manufacturersList?.data?.totalRecords} Manufacturer
                        </span>
                      )}
                    </>
                  ) : (
                    ""
                  )}
                </div>
                {manufacturerListState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : manufacturerListState.error ? (
                  <>
                    <div className="loaderContainer">
                      {manufacturerListState.error}
                    </div>
                  </>
                ) : manufacturerListState.noRecords ? (
                  <div className="loaderContainer text-muted">
                    No manufacturers added yet.
                  </div>
                ) : (
                  <div className="table-responsive manufacturer-table">
                    <CommonTable
                      showPaginationBottom={true}
                      defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                      activePage={pageNum}
                      data={formattedManufacturerList}
                      resizable={false}
                      columns={columns}
                      className="-striped -highlight table-hover"
                      totalPages={Math.ceil(
                        (manufacturersList?.data?.totalFilteredRecords || 0) /
                        manufacturersList?.data?.noOfRecordsPerPage,
                      )}
                      setPageNum={setPageNum}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <RightPanelCard
          isOpen={manufacturerModal}
          toggle={toggleManufacturerModal}
          className="modal-lg"
        >
          <Card className="mb-0">
            <CardHeader
              className="d-flex align-items-center justify-content-between mb-2"
              tag="h4"
            >
              {editState.mode === "ADD"
                ? "Add Manufacturer"
                : "Edit Manufacturer"}
              <i
                className="fa fa-close text-muted pointer"
                onClick={toggleManufacturerModal}
              />
            </CardHeader>

            <Form onSubmit={handleSubmit}>
              <CardBody>
                <Row>
                  <Col md="6">
                    <FormGroup
                      className={`has-label ${formData.manufacturerName.error ? "has-danger" : ""
                        } d-flex flex-column align-items-start`}
                    >
                      <Label for="manufacturerName">
                        Name{" "}
                        <span className="validation-error">*</span>
                      </Label>
                      <Input
                        id="manufacturerName"
                        name="manufacturerName"
                        type="text"
                        placeholder="Enter manufacturer name"
                        autoComplete="off"
                        value={formData.manufacturerName.value || ""}
                        onChange={(e) =>
                          handleInputChange(e.target.name, e.target.value)
                        }
                        className="mb-1"
                        maxLength={100}
                      />
                      {formData.manufacturerName.error && (
                        <small className="validation-error">
                          {formData.manufacturerName.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  {editState.mode === "EDIT" && (
                    <Col md="6">
                      <FormGroup
                        className={`d-flex flex-column align-items-start ${formData.createdBy.error ? "has-danger" : ""
                          }`}
                      >
                        <Label>
                          Created By <span className="validation-error">*</span>
                        </Label>

                        <Select
                          options={CREATED_BY}
                          placeholder="Select created by"
                          className={`text-left ${formData.createdBy.error ? "is-invalid" : ""
                            }`}
                          classNamePrefix="react-select"
                          styles={customStyle}
                          value={CREATED_BY.find(
                            (item) => item.value === formData.createdBy.value,
                          )}
                          onChange={(selectedOption) =>
                            handleInputChange("createdBy", selectedOption.value)
                          }
                          onBlur={() =>
                            handleOnBlur("createdBy", formData.createdBy.value)
                          }
                        />

                        {formData.createdBy.error && (
                          <small className="validation-error">
                            {formData.createdBy.error}
                          </small>
                        )}
                      </FormGroup>
                    </Col>
                  )}
                  <Col md="6">
                    <FormGroup>
                      <Label for="status">
                        Status<span className="validation-error">*</span>
                      </Label>
                      <div className="radio-rapper">
                        <FormGroup check inline>
                          <Label check>
                            <Input
                              type="radio"
                              name="status"
                              value="ACTIVE"
                              checked={formData.status === "ACTIVE"}
                              onChange={(e) =>
                                handleInputChange(e.target.name, e.target.value)
                              }
                            />{" "}
                            Active
                          </Label>
                        </FormGroup>
                        <FormGroup check inline>
                          <Label check>
                            <Input
                              type="radio"
                              name="status"
                              value="INACTIVE"
                              checked={formData.status === "INACTIVE"}
                              onChange={(e) =>
                                handleInputChange(e.target.name, e.target.value)
                              }
                            />{" "}
                            Inactive
                          </Label>
                        </FormGroup>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>

              <CardFooter className="d-flex justify-content-end">
                <Button
                  color="secondary"
                  onClick={() => toggleManufacturerModal()}
                >
                  Cancel
                </Button>
                <Button color="default" type="submit"  disabled={isFormInvalid || manufacturerListState.isSubmitting}>
                  {manufacturerListState.isSubmitting ? (
                    <i className="fa fa-spinner fa-spin fa-lg text-white" />
                  ) : (
                    "Save"
                  )}
                </Button>
              </CardFooter>
            </Form>
          </Card>
        </RightPanelCard>
      </Container>
    </>
  );
};

export default ManufacturerMaster;
